import type { Vector3Tuple } from '@toolproof-core/visualization/_contract/geometry';
import type { EntityItemParametricState } from '@toolproof-core/visualization/_contract/item';
import type { ItemKey } from '@toolproof-core/visualization/_contract/key';

import { FLAG_ABOVE_ITEM_LABEL } from '@/workflow/_data/config/fragments/labels';
import type { Config } from '@/workflow/_contract/config';

export function compositeFlagLabels(args: {
    config: Config;
    sphereRadius: number;
    text: string;
    components: readonly { itemKey: ItemKey<'entity'>; center: Vector3Tuple }[];
}): { itemKey: ItemKey<'entity'>; state: EntityItemParametricState }[] {
    const { config, sphereRadius, text, components } = args;
    if (components.length < 2) throw new Error('A composite flag requires at least two components.');
    const template = config.domainPresentation.entities.resourceTypes.item?.label?.aboveItem ?? FLAG_ABOVE_ITEM_LABEL;
    const midpoint: Vector3Tuple = [0, 0, 0];
    for (const { center } of components) {
        for (const axis of [0, 1, 2] as const) midpoint[axis] += center[axis] / components.length;
    }
    const postHeight = sphereRadius * 1.5;
    const branchHeight = sphereRadius;
    return components.map(({ itemKey, center }, index) => ({
        itemKey,
        state: { label: index !== 0 ? {} : { aboveItem: {
            ...template, text,
            placement: { placementKind: 'item-origin', offset: [
                midpoint[0] - center[0], midpoint[1] - center[1] + sphereRadius + branchHeight + postHeight,
                midpoint[2] - center[2],
            ] },
            flag: {
                postOffset: [0, -postHeight, 0],
                minTextPixels: template.flag ? template.flag.minTextPixels : 1,
                prongOffsets: components.map(component => [
                    component.center[0] - midpoint[0], component.center[1] - midpoint[1] - branchHeight,
                    component.center[2] - midpoint[2],
                ]),
            },
        } } },
    }));
}
